import React, { useState } from 'react';

// MUI Components
import {
  Alert,
  Box,
  Card,
  CardContent,
  Input,
  InputAdornment,
  Typography,
  CircularProgress,
  Snackbar,
  Skeleton
} from '@mui/material';

// MUI Icons
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  QrCode as QrCodeIcon,
  InfoOutlined as InfoOutlinedIcon,
  HourglassBottom as HourglassBottomIcon,
} from "@mui/icons-material";

// Tooltips & Loaders
import HtmlTooltip from '../../components/Tooltip/HtmlTooltip';
import TooltipContent from '../../components/Tooltip/TooltipContent';
import { useAuth } from '../../contexts/AuthContext';

const ResponsiveItemCard = ({ item, index, theme, onRetry, changeQty, editItem, setItemToModify, setOpenDeleteFromBatchModal, setBarcodeLabelProduct, setOpenBarcodeModal, sidebarWidth, isWfs = true }) => {
  const { user, isSubUser } = useAuth();

  const [open, setOpen] = useState(false);
  const [copiedItem, setCopiedItem] = useState("");

  const handleClick = (identifier, value) => {
    navigator.clipboard.writeText(value);
    setCopiedItem(identifier);
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  // Calculate dynamic width for product name based on sidebar width
  const getWidth = () => {
    const baseWidth = 180;
    const widthDifference = sidebarWidth - 320;
    const newWidth = baseWidth + widthDifference;
    return `${newWidth}px`;
  };

  const handleChangeQty = (e) => {
    changeQty(e.target.value, item);
    if (isSubUser) {
      const sentItems = JSON.parse(localStorage.getItem("IFSentItemsSU") || "{}");
      sentItems[user] = sentItems[user].map(a => a.sku === item.sku ? { ...a, qty: e.target.value, unpaidQty: e.target.value } : a);
      localStorage.setItem("IFSentItemsSU", JSON.stringify(sentItems));
    }
  };

  const styles = {
    text: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: '12px',
      cursor: 'copy',
    }
  };

  if (item.status === "UPLOAD") {
    return (
      <Card sx={{ mx: 1, mb: 1, bgcolor: theme.background.primary }} elevation={1}>
        <CardContent sx={{ "&:last-child": { paddingBottom: '5px' }, p: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status Icon Placeholder */}
            <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: theme.background.secondary }} />

            {/* Image */}
            <Skeleton variant="rectangular" width={48} height={48} sx={{ borderRadius: '4px', backgroundColor: theme.background.secondary }} />

            {/* Text Section */}
            <Box sx={{ flex: 1 }}>
              {/* Product Name */}
              <Skeleton variant="text" width="60%" height={20} sx={{ backgroundColor: theme.background.secondary }} />

              {/* Price and Icons Row */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Skeleton variant="text" width="30%" height={18} sx={{ backgroundColor: theme.background.secondary }} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: theme.background.secondary }} />
                  <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: theme.background.secondary }} />
                  <Skeleton variant="circular" width={20} height={20} sx={{ backgroundColor: theme.background.secondary }} />
                </Box>
              </Box>

              {/* Qty Input */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Skeleton variant="text" width={80} height={18} sx={{ backgroundColor: theme.background.secondary }} />
                <Skeleton variant="rectangular" width={60} height={28} sx={{ ml: 2, backgroundColor: theme.background.secondary }} />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card key={index} sx={{ mx: 1, mb: 1, bgcolor: theme.background.primary, }} elevation={1}>
        <CardContent sx={{ "&:last-child": { paddingBottom: '5px' }, p: '5px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {(item.status === "LOADING" || item.status === "TIME_BRAKE") && (
              <Box sx={{ display: 'flex' }}>
                <HtmlTooltip
                  customtheme={theme}
                  title={
                    <Typography variant='body2' sx={{ fontFamily: theme.fontFamily, color: theme.text.primary }}>Step {item.step} of {isWfs ? "6" : " 3"}</Typography>
                  }
                  arrow
                  placement="left"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <CircularProgress size="20px" sx={{ color: theme.color.primary, }} />
                </HtmlTooltip>
              </Box>
            )}
            {item.status === "PENDING" && (
              <Box sx={{ display: 'flex' }}>
                <HtmlTooltip
                  customtheme={theme}
                  title={
                    <Typography sx={{ fontFamily: theme.fontFamily, fontSize: '14px' }}>Press the 'Start Process' button below to begin uploading all items in this section.</Typography>
                  }
                  arrow
                  placement="top"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <HourglassBottomIcon size="20px" sx={{ color: theme.color.primary, }} />
                </HtmlTooltip>
              </Box>
            )}
            {item.status === "SUCCESS" && (
              <Box sx={{ display: 'flex' }}>
                <CheckCircleIcon size="20px" sx={{ color: theme.accent.success, }} />
              </Box>
            )}
            {item.status === "WARNING" && (
              <Box sx={{ display: 'flex' }}>
                <HtmlTooltip
                  customtheme={theme}
                  title={
                    <TooltipContent
                      reasons={item.unpublishedReasons.reason}
                      theme={theme}
                      onRetry={() => onRetry(item)}
                    />
                  }
                  arrow
                  placement="left"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <WarningIcon size="20px" sx={{ color: theme.accent.warning, }} />
                </HtmlTooltip>
              </Box>
            )}
            {item.status === "ERROR" && (
              <Box sx={{ display: 'flex' }}>
                <HtmlTooltip
                  customtheme={theme}
                  title={
                    <TooltipContent
                      reasons={item.unpublishedReasons.reason}
                      theme={theme}
                      onRetry={() => onRetry(item)}
                    />
                  }
                  arrow
                  placement="left"
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -5],
                          },
                        },
                      ],
                    },
                  }}
                >
                  <ErrorIcon size="20px" sx={{ color: theme.accent.error, }} />
                </HtmlTooltip>
              </Box>
            )}
            <img
              src={item.image}
              alt={item.productName}
              style={{
                width: '48px',
                height: '48px',
                objectFit: 'cover',
                borderRadius: '4px'
              }}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
              }}
            />
            <Box>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      color: theme.text.primary,
                      fontFamily: theme.fontFamily,
                    }}
                  >
                    {item.productName}
                  </Typography>
                }
                arrow
                placement="left"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -5],
                        },
                      },
                    ],
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    width: getWidth(), // Dynamic width based on sidebar width
                    color: theme.text.primary,
                    fontFamily: theme.fontFamily,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => window.open(`https://www.walmart.com/ip/${item.itemId}`)}
                >
                  {item.productName}
                </Typography>
              </HtmlTooltip>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.8rem', color: theme.text.secondary, fontFamily: theme.fontFamily }}>
                  ${typeof item.price?.amount === "number" ? item.price.amount.toFixed(2) : "N/A"}
                </Typography>

                <Box>
                  <HtmlTooltip
                    customtheme={theme}
                    title={<Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '12px', }}>Click to download Barcode label</Typography>}
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -5],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <QrCodeIcon size="12px" sx={{ cursor: 'pointer', color: theme.text.primary, }} onClick={() => { setBarcodeLabelProduct(item); setOpenBarcodeModal(true); }} />
                  </HtmlTooltip>

                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Box>
                        <Typography sx={styles.text} onClick={() => handleClick('SKU', item.sku)}>SKU: {item.sku}</Typography>
                        <Typography sx={styles.text} onClick={() => handleClick('Item ID', item.itemId)}>Item ID: {item.itemId}</Typography>
                        <Typography sx={styles.text} onClick={() => handleClick('UPC', item.upc)}>UPC: {item.upc}</Typography>
                        <Typography sx={styles.text} onClick={() => handleClick('Feed ID', item.feedId)}>Feed ID: {item.feedId.slice(0, 15)}...</Typography>
                        {item.wfsFeedId && <Typography sx={styles.text} onClick={() => handleClick('WFS Feed ID', item.wfsFeedId)}>WFS Feed ID: {item.wfsFeedId}</Typography>}
                      </Box>
                    }
                    arrow
                    placement="left"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -5],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <InfoOutlinedIcon size="12px" sx={{ color: theme.text.primary, }} />
                  </HtmlTooltip>

                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ fontSize: '0.8rem', color: theme.text.secondary, fontFamily: theme.fontFamily, mr: '10px' }}>
                        Edit item
                      </Typography>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -5],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <EditIcon size="20px" sx={{ color: theme.color.primary, cursor: 'pointer' }} onClick={() => editItem(item)} />
                  </HtmlTooltip>

                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: '12px', }}>Remove from batch</Typography>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -5],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <DeleteIcon size="12px" sx={{ cursor: 'pointer', color: theme.accent.error, }} onClick={() => { setItemToModify(item); setOpenDeleteFromBatchModal(true); }} />
                  </HtmlTooltip>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.8rem', color: theme.text.secondary, fontFamily: theme.fontFamily, mr: '10px' }}>
                  Change Qty:
                </Typography>

                <Input
                  type="number"
                  placeholder={item?.qty?.toString() || "0"}
                  onBlur={(e) => handleChangeQty(e)}
                  sx={{
                    fontFamily: theme.fontFamily,
                    color: theme.text.primary,
                    fontSize: 14,
                    py: '10px',
                    width: '35%',
                    py: 0,
                    '&.MuiInput-underline:before': {
                      borderBottomColor: theme.text.primary
                    },
                    '&.MuiInput-underline:after': {
                      borderBottomColor: theme.text.primary
                    }
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Typography
                        sx={{
                          fontFamily: theme.fontFamily,
                          color: theme.text.primary,
                          fontSize: 14,
                          py: 0
                        }}>
                        #
                      </Typography>
                    </InputAdornment>
                  }
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          variant="filled"
          sx={{ width: '100%', fontFamily: theme.fontFamily, bgcolor: theme.accent.success }}
        >
          {copiedItem} copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResponsiveItemCard;